import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import {
  OCEAN_SUNSET_LIGHT,
  OCEAN_SUNSET_DARK,
  buildThemeObject,
} from '../components/finops/theme-tokens';

export const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const themes = {
  'ocean-light': buildThemeObject(OCEAN_SUNSET_LIGHT),
  'ocean-dark': buildThemeObject(OCEAN_SUNSET_DARK),
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('app-theme');
    if (savedTheme && themes[savedTheme]) return savedTheme;
    if (savedTheme === 'light-silver-gold' || savedTheme === 'dark-silver') {
      const migrated = savedTheme === 'dark-silver' ? 'ocean-dark' : 'ocean-light';
      localStorage.setItem('app-theme', migrated);
      return migrated;
    }
    return 'ocean-light';
  });

  const themeConfig = themes[currentTheme];

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(themeConfig).forEach(([key, value]) => {
      if (key !== 'name' && key !== 'mode') {
        root.style.setProperty(`--theme-${key}`, value);
      }
    });
  }, [themeConfig]);

  const changeTheme = useCallback((themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('app-theme', themeName);
    }
  }, []);

  const antdThemeConfig = useMemo(() => ({
    algorithm: themeConfig.mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: themeConfig.dataBlue,
      colorInfo: themeConfig.dataBlue,
      colorSuccess: themeConfig.green,
      borderRadius: 6,
      colorBgContainer: themeConfig.surface,
      colorBgElevated: themeConfig.surface,
    },
    components: {
      Menu: {
        itemSelectedBg: themeConfig.blueLight || '#E0EEF1',
        itemSelectedColor: themeConfig.dataBlue,
        itemHoverBg: themeConfig.hoverBg || 'rgba(0,95,115,0.04)',
        itemHoverColor: themeConfig.dataBlue,
        groupTitleColor: themeConfig.textTertiary,
        groupTitleFontSize: 11,
        itemBorderRadius: 10,
        itemHeight: 40,
        itemMarginInline: 10,
        fontSize: 15,
      },
    },
  }), [themeConfig]);

  const value = useMemo(() => ({
    currentTheme,
    themeConfig,
    themes,
    changeTheme,
  }), [currentTheme, themeConfig, changeTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdThemeConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
